import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IExemptionSearch } from "../../domain/IExemptionSearch";
import { IInput } from "../../domain/IInput";
import { IGetInputsRequestParams } from "../../domain/IGetInputsRequestParams";
import { consumablesService } from "../../services/consumables.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IOutputListItem } from "../../domain/IOutputListItem";
import { IConsumablesInputUpdate } from "../../domain/IConsumablesInputUpdate";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";

export const getInputs = createAsyncThunk(
  "Consumables/getInputs",
  async (
    params: {
      getInputsRequestParams: IGetInputsRequestParams,
      pagingParams: IGetPageRequestParams,
      abortSignal: AbortSignal,
      updateStateAfterFetch: (updateStateParams: IUpdateStateAfterSearchParams) => void
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await consumablesService.getInputs(
        params.getInputsRequestParams,
        params.pagingParams,
        params.abortSignal
      );
      return {
        response,
        pagingData: params.pagingParams,
        updateStateAfterFetch: params.updateStateAfterFetch
      };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateInput = createAsyncThunk(
  "Consumables/updateInput",
  async (data: IConsumablesInputUpdate, { rejectWithValue }) => {
    try {
      return await consumablesService.updateInput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);


export const getExemptions = createAsyncThunk(
  "Consumables/getExemptionsForInputPage",
  async (data: IExemptionSearch) => {
    try {
      const abortCtrl = new AbortController();
      const result = await consumablesService.getExemptions(data, { fetchMode: "replace" }, abortCtrl.signal);
      return result.listItems;
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteInput = createAsyncThunk(
  "Consumables/deleteInput",
  async (inputId: number) => {
    try {
      await consumablesService.deleteInput(inputId);
      return inputId;
    } catch (err) {
      setError(err);
    }
  }
);

interface IUpdateInputItemPopoverState {
  isOpen: boolean;
  top?: number;
  left?: number;
  offsetTop?: number;
  offsetLeft?: number;
  itemRowIndex: number;
  focus?: string;
}

interface inputState {
  inputsLoading: boolean;
  exemptionsLoading: boolean;
  inputs: IInput[];
  totalQuantityAmountTotal: number;
  totalQuantityAmountRemaining: number;
  totalInitialValueAllInputs: number;
  totalRemainingValueAllInputs: number;
  outputListItems: IOutputListItem[];
  selectedIndex: number;
  error: any;
  deleteInputModalOpen: boolean;
  indexForOperation: number;
  newInputPopover: IPopoverState;
  updateInputItemPopover: IUpdateInputItemPopoverState;
  searchInputPopover: IPopoverState;
}

const initialState: inputState = {
  inputsLoading: false,
  exemptionsLoading: false,
  inputs: [],
  totalQuantityAmountTotal: 0,
  totalQuantityAmountRemaining: 0,
  totalInitialValueAllInputs: 0,
  totalRemainingValueAllInputs: 0,
  outputListItems: [],
  selectedIndex: 0,
  error: null,
  deleteInputModalOpen: false,
  newInputPopover: { isOpen: false },
  indexForOperation: -1,
  updateInputItemPopover: {
    isOpen: false,
    itemRowIndex: -1,
    top: 0,
    left: 0,
  },
  searchInputPopover: { isOpen: false },
};

export const inputSlice = createSlice({
  name: "consumables-input",
  initialState: initialState,
  reducers: {
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setDeleteInputModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteInputModalOpen = action.payload.visible;
      state.indexForOperation = action.payload.index;
    },
    setNewInputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newInputPopover = action.payload;
    },
    setSearchInputPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchInputPopover = action.payload;
    },
    setUpdateInputItemPopover: (
      state,
      action: PayloadAction<IUpdateInputItemPopoverState>
    ) => {
      state.updateInputItemPopover = action.payload;
    },
    addNewInput: (state, action: PayloadAction<IInput>) => {
      if (action.payload) {
        state.inputs = [action.payload, ...state.inputs];
        state.totalInitialValueAllInputs += action.payload.totalInitialValue;
        state.totalRemainingValueAllInputs += action.payload.totalRemainingValue;
        state.totalQuantityAmountRemaining += action.payload.quantityAmountRemaining;
        state.totalQuantityAmountTotal += action.payload.quantityAmountTotal;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInputs.pending, (state) => {
      state.inputsLoading = true;
    });
    builder.addCase(getInputs.fulfilled, (state, action) => {
      state.inputsLoading = false;
      if (action.payload) {
        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = action.payload.response.inputs;
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          state.selectedIndex = 0;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...action.payload.response.inputs, ...state.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.inputs.length;
          const newInputs = [...state.inputs, ...action.payload.response.inputs];
          const newFirstElementId = newInputs?.[0]?.inputId;
          const newLastElementId = action.payload.response.inputs?.[elementsFetched - 1]?.inputId;
          state.inputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }
        state.totalInitialValueAllInputs = action.payload.response.totalInitialValueAllInputs;
        state.totalRemainingValueAllInputs = action.payload.response.totalRemainingValueAllInputs;
        state.totalQuantityAmountRemaining = action.payload.response.totalQuantityAmountRemaining;
        state.totalQuantityAmountTotal = action.payload.response.totalQuantityAmountTotal;
      }
    });
    builder.addCase(getInputs.rejected, (state, action) => {
      state.inputsLoading = false;
      state.error = action.error;
    });
    builder.addCase(updateInput.pending, (state) => {
    });
    builder.addCase(updateInput.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach(newInput => {
          const index = state.inputs.findIndex(i => i.inputId === newInput.inputId);
          if (index !== -1) {
            state.inputs[index] = newInput;
          }
        });
      }
    });
    builder.addCase(updateInput.rejected, (state, action) => {
      state.error = action.error;
    });

    builder.addCase(getExemptions.pending, (state) => {
      state.exemptionsLoading = true;
    });
    builder.addCase(getExemptions.fulfilled, (state, action) => {
      state.outputListItems = action.payload ? action.payload : [];
      state.exemptionsLoading = false;
    });
    builder.addCase(getExemptions.rejected, (state, action) => {
      state.exemptionsLoading = false;
      state.error = action.error;
    });
    builder.addCase(deleteInput.pending, (state) => {
      state.inputsLoading = true;
    });
    builder.addCase(deleteInput.fulfilled, (state, action) => {
      state.inputsLoading = false;
      if (action.payload) {
        state.inputs = state.inputs.filter((i) => i.inputId !== action.payload);
      }
    });
    builder.addCase(deleteInput.rejected, (state, action) => {
      state.inputsLoading = false;
      state.error = action.error;
    });
  },
});

export const {
  setError,
  setSelectedIndex,
  setDeleteInputModalOpen,
  setNewInputPopover,
  setSearchInputPopover,
  setUpdateInputItemPopover,
  addNewInput,
} = inputSlice.actions;

export const selectState = (state: RootState) => state.consumables.input;
export const selectInput = (state: RootState) =>
  state.consumables.input.inputs[state.consumables.input.selectedIndex];
export const selectInputForOperation = (state: RootState) =>
  state.consumables.input.inputs[state.consumables.input.indexForOperation];
export const selectInputForUpdateItem = (state: RootState) =>
  state.consumables.input.inputs[state.consumables.input.updateInputItemPopover.itemRowIndex];
export default inputSlice.reducer;
